import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { articles, columns } from "../../mocks/articles";
import { MiniArticleRow } from "../home/components/ArticleCard";
import NewsletterForm from "../../components/feature/NewsletterForm";
import { useSeo } from "../../hooks/useSeo";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  useSeo({
    title: article ? `${article.title} | 今日瓜报` : "内容不存在 | 今日瓜报",
    description: article ? article.excerpt : "未找到该文章内容。",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.title,
            description: article.excerpt,
            image: [article.image],
            datePublished: `${article.date}T09:00:00+08:00`,
            dateModified: `${article.date}T09:00:00+08:00`,
            author: { "@type": "Organization", name: "今日瓜报编辑部" },
            publisher: {
              "@type": "Organization",
              name: "今日瓜报",
              logo: { "@type": "ImageObject", url: "https://ocbdo.cn/" },
            },
            mainEntityOfPage: `https://ocbdo.cn/article/${article.slug}`,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://ocbdo.cn/" },
              { "@type": "ListItem", position: 2, name: article.kicker, item: `https://ocbdo.cn/#${article.category}` },
              { "@type": "ListItem", position: 3, name: article.title, item: `https://ocbdo.cn/article/${article.slug}` },
            ],
          },
        ]
      : [],
  });

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="mx-auto flex max-w-3xl flex-1 flex-col items-center justify-center px-4 py-32 text-center">
          <i className="ri-error-warning-line text-5xl text-secondary-300" aria-hidden="true"></i>
          <h1 className="mt-4 font-serif text-2xl font-black text-foreground-950">这篇文章不存在或已下线</h1>
          <p className="mt-2 text-sm text-foreground-600">可能是链接有误，不妨回到首页看看其他精彩内容。</p>
          <Link to="/" className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 hover:bg-primary-600 transition-colors">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = articles
    .filter((a) => a.slug !== article.slug && (a.category === article.category || a.tag === article.tag))
    .slice(0, 4);
  const more =
    related.length < 4
      ? articles.filter((a) => a.slug !== article.slug && !related.includes(a)).slice(0, 4 - related.length)
      : [];

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Navbar />
      <main>
        {/* 面包屑 */}
        <div className="pt-28 md:pt-32">
          <div className="mx-auto max-w-4xl px-4 md:px-6">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-700">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <Link to={`/#${article.category}`} className="hover:text-primary-700">{article.kicker}</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-800 truncate max-w-[40vw]">{article.title}</span>
            </nav>

            <header className="mt-6">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">{article.kicker}</span>
                <span className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">#{article.tag}</span>
              </div>
              <h1 className="mt-4 font-serif text-2xl font-black leading-snug text-foreground-950 md:text-4xl">{article.title}</h1>
              <p className="mt-4 text-[15px] leading-relaxed text-foreground-600 md:text-base">{article.excerpt}</p>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-sm text-foreground-600">
                <span className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-600 text-sm text-background-50">编</span>
                  <span><strong className="block text-foreground-900">{article.author}</strong>责任编辑</span>
                </span>
                <span className="flex items-center gap-1"><i className="ri-calendar-2-line" aria-hidden="true"></i>{article.date}</span>
                <span className="flex items-center gap-1"><i className="ri-time-line" aria-hidden="true"></i>约 {article.readMin} 分钟</span>
                <span className="flex items-center gap-1"><i className="ri-fire-line text-primary-500" aria-hidden="true"></i>{article.heat} 热度</span>
              </div>
            </header>

            {/* 主图 */}
            <figure className="mt-6 overflow-hidden rounded-xl">
              <img src={article.image} alt={article.imageAlt} title={`${article.title} - 今日瓜报`} className="w-full object-cover" />
            </figure>

            {/* 正文 */}
            <article className="prose-editorial mt-8 space-y-6">
              {article.content.map((p, i) => (
                <p key={i} className="text-base md:text-[17px] text-foreground-800">{p}</p>
              ))}
            </article>

            {/* 标签 */}
            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="text-sm text-foreground-600">本文标签：</span>
              {columns.map((c) => c.name).filter((n) => n === article.kicker).map((n) => (
                <span key={n} className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-800">{n}</span>
              ))}
              <span className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">#{article.tag}</span>
            </div>

            {/* 作者栏 */}
            <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-xl bg-background-100 p-6 sm:flex-row sm:items-center">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-lg font-bold text-background-50">编</span>
                <div>
                  <p className="font-serif text-base font-bold text-foreground-950">{article.author}</p>
                  <p className="text-sm text-foreground-600">今日瓜报编辑部 · 坚持信源求证与客观复盘</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 text-sm text-foreground-600">觉得不错</span>
                <button className="flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600 transition-colors">
                  <i className="ri-thumb-up-line" aria-hidden="true"></i>点赞
                </button>
                <button className="flex items-center gap-1 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm text-foreground-700 hover:border-primary-300 transition-colors">
                  <i className="ri-share-forward-line" aria-hidden="true"></i>分享
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 相关推荐 */}
        <section className="mt-12 bg-background-100/70 py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-serif text-xl font-black text-foreground-950 md:text-2xl">
                <i className="ri-refresh-line text-primary-600" aria-hidden="true"></i>相关推荐
              </h2>
              <Link to="/#feed" className="text-sm text-primary-700 hover:underline">查看全部热稿 →</Link>
            </div>
            <div className="mt-6 space-y-5">
              {[...related, ...more].slice(0, 4).map((a) => (
                <MiniArticleRow key={a.slug} article={a} />
              ))}
            </div>
          </div>
        </section>

        {/* 文末订阅 */}
        <section className="bg-foreground-950 py-12">
          <div className="mx-auto max-w-3xl px-4 md:px-6 text-center">
            <h2 className="font-serif text-2xl font-black text-background-50">喜欢这篇深度内容？</h2>
            <p className="mt-2 text-sm text-background-100/75">订阅今日瓜报，更多头条解读、重点瓜分析与深度报道准时送达。</p>
            <div className="mx-auto mt-5 max-w-md">
              <NewsletterForm />
            </div>
          </div>
        </section>
      </main>

      {/* 返回顶部 */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="返回顶部"
          className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-foreground-950 text-background-50 shadow-lg hover:bg-primary-600 transition-colors"
        >
          <i className="ri-arrow-up-line text-lg" aria-hidden="true"></i>
        </button>
      )}
      <Footer />
    </div>
  );
}